import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSchoolSettings } from '@/context/SchoolSettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Trophy, Award, AlertCircle, TrendingUp, Brain, Calendar, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import MessageBell from '@/components/MessageBell';
import BookChat from '@/components/BookChat';
import ProfileSettings from '@/components/ProfileSettings';
import GamificationPanel from '@/components/GamificationPanel';
import MobileBottomNav from '@/components/MobileBottomNav';
import YearlyProgress from '@/components/YearlyProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Merit Plus Light Theme Colors
const colors = {
  canvas: '#F8FAFC',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  cardHover: '#F1F5F9',
  teal: '#0D9488',
  tealHover: '#0F766E',
  tealLight: '#CCFBF1',
  indigo: '#4F46E5',
  indigoLight: '#E0E7FF',
  merit: '#10B981',
  meritBg: '#ECFDF5',
  demerit: '#EF4444',
  demeritBg: '#FEF2F2',
  gold: '#F59E0B',
  goldBg: '#FEF3C7',
  purple: '#8B5CF6',
  purpleBg: '#EDE9FE',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  border: '#E2E8F0',
};

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png";

export default function StudentDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const { settings: schoolSettings } = useSchoolSettings();
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const [score, setScore] = useState(null);
  const [records, setRecords] = useState([]);
  const [classAverage, setClassAverage] = useState(null);
  const [badges, setBadges] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scoreRes, recordsRes, avgRes, badgesRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/student/score`, { headers: getAuthHeaders() }),
        axios.get(`${API}/student/records`, { headers: getAuthHeaders() }),
        axios.get(`${API}/student/class-average`, { headers: getAuthHeaders() }),
        axios.get(`${API}/students/${user?.id}/badges`, { headers: getAuthHeaders() }),
        axios.get(`${API}/announcements`, { headers: getAuthHeaders() }),
      ]);
      setScore(scoreRes.data);
      setRecords(recordsRes.data);
      setClassAverage(avgRes.data);
      setBadges(badgesRes.data);
      setAnnouncements(announcementsRes.data.announcements || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('school_subdomain');
    navigate(subdomain ? `/${subdomain}` : '/');
    toast.success('Сәтті шықтыңыз');
  };

  const fetchAiAnalysis = async () => {
    setLoadingAi(true);
    try {
      const response = await axios.get(`${API}/student/ai-analysis`, { headers: getAuthHeaders() });
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      toast.error('AI талдау қолжетімсіз');
    } finally {
      setLoadingAi(false);
    }
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.canvas }}
      >
        <div className="flex flex-col items-center gap-4">
          <div 
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: `${colors.teal} transparent ${colors.teal} ${colors.teal}` }}
          />
          <span style={{ color: colors.textSecondary }}>Жүктелуде...</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-20 sm:pb-0" 
      data-testid="student-dashboard"
      style={{ backgroundColor: colors.canvas, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {/* Background Gradient Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{ backgroundColor: `${colors.surface}ee`, borderColor: colors.border }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src={schoolSettings?.logo_url || LOGO_URL}
                alt={schoolSettings?.school_name || 'Merit Plus'}
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
              <div>
                <h1 
                  className="text-lg sm:text-xl font-bold tracking-tight"
                  style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                >
                  {schoolSettings?.school_name || 'Merit Plus'}
                </h1>
                <p className="text-xs sm:text-sm font-medium hidden sm:block" style={{ color: colors.teal }}>
                  Оқушы панелі
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold" style={{ color: colors.textPrimary }}>{user?.full_name}</p>
                <p className="text-xs" style={{ color: colors.textSecondary }}>{score?.class_name}</p>
              </div>
              <MessageBell 
                className="transition-colors border"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textPrimary }}
              />
              <ProfileSettings user={user} />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleLogout}
                className="transition-all hover:scale-105 w-9 h-9 sm:w-10 sm:h-10 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textSecondary }}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-8">
          {/* Score Overview Header */}
          <div>
            <h2 
              className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
              style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
            >
              Менің ұпайларым
            </h2>
            <p style={{ color: colors.textSecondary }}>Ағымдағы айдың нәтижелері</p>
          </div>

          {/* Score Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Score */}
            <div 
              className="relative overflow-hidden rounded-2xl p-8 text-center transition-all hover:scale-[1.02]"
              style={{ 
                background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealHover} 100%)`,
                boxShadow: `0 20px 40px ${colors.teal}40`
              }}
            >
              <div 
                className="absolute top-0 right-0 w-40 h-40 rounded-full -mr-20 -mt-20"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              />
              <div className="relative">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                >
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <div 
                  className="text-5xl sm:text-6xl font-bold text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {score?.total_score >= 0 ? '+' : ''}{score?.total_score || 0}
                </div>
                <p className="text-sm text-white/80 mt-2 font-medium">Жалпы ұпай</p>
                <div className="mt-4 flex justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="w-4 h-4"
                      fill={i < Math.min(5, Math.floor((score?.total_score || 0) / 20)) ? 'white' : 'transparent'}
                      style={{ color: i < Math.min(5, Math.floor((score?.total_score || 0) / 20)) ? 'white' : 'rgba(255,255,255,0.3)' }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Merit Count */}
            <div 
              className="relative overflow-hidden rounded-2xl p-8 text-center transition-all hover:scale-[1.02]"
              style={{ 
                background: `linear-gradient(135deg, ${colors.merit} 0%, #059669 100%)`,
                boxShadow: `0 20px 40px ${colors.merit}40`
              }}
            >
              <div 
                className="absolute top-0 right-0 w-40 h-40 rounded-full -mr-20 -mt-20"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              />
              <div className="relative">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                >
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div 
                  className="text-5xl sm:text-6xl font-bold text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {score?.merit_count || 0}
                </div>
                <p className="text-sm text-white/80 mt-2 font-medium">Марапат</p>
                <p className="text-xs text-white/60 mt-1">+{score?.merit_points || 0} ұпай</p>
              </div>
            </div>

            {/* Demerit Count */}
            <div 
              className="relative overflow-hidden rounded-2xl p-8 text-center transition-all hover:scale-[1.02]"
              style={{ 
                background: `linear-gradient(135deg, ${colors.demerit} 0%, #DC2626 100%)`,
                boxShadow: `0 20px 40px ${colors.demerit}40`
              }}
            >
              <div 
                className="absolute top-0 right-0 w-40 h-40 rounded-full -mr-20 -mt-20"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              />
              <div className="relative">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                >
                  <AlertCircle className="w-8 h-8 text-white" />
                </div>
                <div 
                  className="text-5xl sm:text-6xl font-bold text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {score?.demerit_count || 0}
                </div>
                <p className="text-sm text-white/80 mt-2 font-medium">Ескерту</p>
                <p className="text-xs text-white/60 mt-1">-{score?.demerit_points || 0} ұпай</p>
              </div>
            </div>
          </div>

          {/* Class Comparison */}
          {classAverage && (
            <div 
              className="rounded-2xl p-6 border"
              style={{ backgroundColor: colors.card, borderColor: colors.border }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.tealLight }}
                >
                  <TrendingUp className="w-5 h-5" style={{ color: colors.teal }} />
                </div>
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                >
                  Сынып орташасымен салыстыру
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: colors.textSecondary }}>Сынып орташасы ({classAverage.class_name}):</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                  >
                    {classAverage.average}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: colors.textSecondary }}>Менің ұпайым:</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                  >
                    {score?.total_score || 0}
                  </span>
                </div>
                <div 
                  className="h-3 rounded-full overflow-hidden"
                  style={{ backgroundColor: colors.surface }}
                >
                  <div 
                    className="h-full rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(100, classAverage.average > 0 ? ((score?.total_score || 0) / classAverage.average) * 100 : 0)}%`,
                      background: `linear-gradient(90deg, ${colors.teal}, ${colors.merit})`
                    }}
                  />
                </div>
                {(score?.total_score || 0) >= classAverage.average ? (
                  <p className="text-sm font-medium" style={{ color: colors.merit }}>
                    Сынып орташасынан жоғарысыз! Өте жақсы!
                  </p>
                ) : (
                  <p className="text-sm" style={{ color: colors.textSecondary }}>
                    Сынып орташасына жетуге тырысыңыз!
                  </p>
                )}
              </div>
            </div>
          )}

          {/* AI Analysis Section */}
          <div 
            className="rounded-2xl p-6 border"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.purpleBg }}
                >
                  <Brain className="w-5 h-5" style={{ color: colors.purple }} />
                </div>
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                >
                  AI Талдау (Тәрбие анализі)
                </h3>
              </div>
              <Button 
                variant="outline"
                size="sm"
                onClick={fetchAiAnalysis}
                disabled={loadingAi}
                className="hover:bg-purple-50"
                style={{ borderColor: colors.border, color: colors.purple }}
              >
                {loadingAi ? 'Жүктелуде...' : aiAnalysis ? 'Қайта талдау' : 'Талдау жасау'}
              </Button>
            </div>
            <p className="text-xs mb-4" style={{ color: colors.textMuted }}>
              Жеке AI кеңесшіңіз сіздің мінез-құлқыңызды талдайды
            </p>
            {aiAnalysis ? (
              <div 
                className="rounded-xl p-4 text-sm whitespace-pre-wrap"
                style={{ backgroundColor: colors.purpleBg, color: colors.textPrimary }}
              >
                {aiAnalysis}
              </div>
            ) : (
              <div className="text-center py-6">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
                <p style={{ color: colors.textMuted }}>AI талдауын жасау үшін жоғарыдағы батырманы басыңыз</p>
              </div>
            )}
          </div>

          {/* Yearly Progress & History */}
          <div 
            className="rounded-2xl p-6 border"
            style={{ backgroundColor: colors.card, borderColor: colors.border }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.indigoLight }}
              >
                <Calendar className="w-5 h-5" style={{ color: colors.indigo }} />
              </div>
              <div>
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                >
                  Жылдық прогрес және тарих
                </h3>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  Бүкіл жыл бойынша жетістіктеріңіз және мінез-құлық тарихыңыз
                </p>
              </div>
            </div>
            <YearlyProgress 
              studentId={user?.id}
              studentName={user?.full_name}
              getAuthHeaders={getAuthHeaders}
              isOwnProfile={true}
            />
          </div>

          {/* Book Chat */}
          <div data-section="bookchat">
            <BookChat />
          </div>

          {/* Gamification Panel */}
          <div data-section="gamification">
            {badges && (
              <GamificationPanel badgesData={badges} studentId={user?.id} />
            )}
          </div>

          {/* Announcements */}
          {announcements.length > 0 && (
            <div 
              className="rounded-2xl p-6 border"
              style={{ 
                backgroundColor: colors.card, 
                borderColor: colors.border
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.goldBg }}
                >
                  <AlertCircle className="w-5 h-5" style={{ color: colors.gold }} />
                </div>
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
                >
                  Хабарландырулар
                </h3>
              </div>
              <div className="space-y-3">
                {announcements.slice(0, 3).map((announcement) => (
                  <div 
                    key={announcement.id}
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: colors.goldBg }}
                  >
                    <h4 className="font-bold mb-1" style={{ color: colors.textPrimary }}>{announcement.title}</h4>
                    <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>{announcement.message}</p>
                    <div className="flex items-center justify-between text-xs" style={{ color: colors.textMuted }}>
                      <span>{announcement.created_by}</span>
                      <span>{new Date(announcement.created_at).toLocaleDateString('kk-KZ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Records List */}
          <div>
            <h3 
              className="text-2xl font-semibold mb-4"
              style={{ fontFamily: "'Outfit', sans-serif", color: colors.textPrimary }}
            >
              Менің жазбаларым
            </h3>
            {records.length === 0 ? (
              <div 
                className="py-12 text-center rounded-2xl border"
                style={{ backgroundColor: colors.card, borderColor: colors.border }}
              >
                <p style={{ color: colors.textMuted }}>Жазбалар жоқ</p>
              </div>
            ) : (
              <div className="space-y-3">
                {records.map((record) => (
                  <div 
                    key={record.id} 
                    className="rounded-2xl p-5 border"
                    style={{ backgroundColor: colors.card, borderColor: colors.border }}
                    data-testid={`record-${record.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {record.behavior_type === 'merit' ? (
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${colors.merit}20` }}
                            >
                              <Award className="w-4 h-4" style={{ color: colors.merit }} />
                            </div>
                          ) : (
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${colors.demerit}20` }}
                            >
                              <AlertCircle className="w-4 h-4" style={{ color: colors.demerit }} />
                            </div>
                          )}
                          <h4 className="font-semibold text-lg" style={{ color: colors.textPrimary }}>
                            {record.behavior_name}
                          </h4>
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-bold"
                            style={{ 
                              backgroundColor: record.behavior_type === 'merit' ? `${colors.merit}20` : `${colors.demerit}20`,
                              color: record.behavior_type === 'merit' ? colors.merit : colors.demerit
                            }}
                          >
                            {record.behavior_type === 'merit' ? '+' : '-'}{record.points} ұпай
                          </span>
                        </div>
                        <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>
                          Мұғалім: {record.teacher_name}
                        </p>
                        {record.comment && (
                          <p 
                            className="text-sm p-3 rounded-lg mt-2"
                            style={{ backgroundColor: colors.surface, color: colors.textSecondary }}
                          >
                            {record.comment}
                          </p>
                        )}
                        <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                          {format(new Date(record.created_at), 'dd MMM yyyy, HH:mm')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav role="student" />
    </div>
  );
}